/**
 * First-paint disclosure of a school's booking fee on the shared-UI checkouts
 * (webcomponents / Website v2, Youmo/Discover) — ruling R26, the shared-UI
 * twin of the widget's note (apps/widget useBasketCheckout.ts).
 *
 * The rate comes from `bookingFeeDisclosure` on
 * `GET /widget/school/:id/checkout-config`, which every one of these checkouts
 * already fetches. The rate prices a first-paint *estimate*
 * (`estimateBookingFeePence`) so the Total is fee-inclusive before any session
 * exists; the charged amount is still the session's `bookingFeePence`, which
 * replaces the estimate as soon as it lands.
 */

import { computeBookingFee } from "@obby/lib";

/** Mirrors the API's `bookingFeeDisclosure` (GetSchoolCheckoutConfig.controller.ts). */
export type BookingFeeDisclosure = {
  applies: boolean;
  percentBps: number;
  fixedMinor: number;
  refundOnCustomerCancel: boolean;
};

type FormatPrice = (minor: number, currency: string) => string;

/** "3.6% + £0.50", "3%", "£0.50" — or null when there is no fee to describe. */
export function bookingFeeRateText(
  disclosure: BookingFeeDisclosure | null | undefined,
  currency: string,
  formatPrice: FormatPrice,
): string | null {
  if (!disclosure?.applies) return null;
  const bps = Number(disclosure.percentBps) || 0;
  const fixed = Number(disclosure.fixedMinor) || 0;
  const pct = bps > 0 ? `${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 2)}%` : null;
  const flat = fixed > 0 ? formatPrice(fixed, currency) : null;
  const text = [pct, flat].filter(Boolean).join(" + ");
  return text || null;
}

/**
 * The rate/refund sentence for the fee row's tooltip. Each half is only said
 * when the disclosure loaded: an empty rate would read "A booking fee of
 * applies…", and a refund promise the school never made is worse than none.
 */
export function bookingFeeDetailText(
  disclosure: BookingFeeDisclosure | null | undefined,
  currency: string,
  formatPrice: FormatPrice,
): string | null {
  const rate = bookingFeeRateText(disclosure, currency, formatPrice);
  if (!rate || !disclosure) return null;
  return `A booking fee of ${rate} applies to all bookings. ${
    disclosure.refundOnCustomerCancel
      ? "It is refunded if you cancel."
      : "It is not refunded if you cancel."
  }`;
}

export type BookingFeeEstimateContext = {
  disclosure: BookingFeeDisclosure | null | undefined;
  /** What the customer pays now, after discounts and after any gift card, before any fee. */
  payNowMinor: number;
  /**
   * Gift-card value applied to this order. Added back into the base (ruling
   * R24: a gift card is a payment, not a price change) — the server's
   * `orderContractSubtotal` does the same.
   */
  giftCardAppliedMinor?: number;
  /** Deposit baskets: the (already discounted) balance owed later, also part of the contract base. */
  laterBalanceMinor?: number;
  /** At least one booking in the basket (products / gift cards / packs alone carry no fee — R23). */
  hasBookingItem: boolean;
  /** Paid entirely from a bundle's credits or a membership. */
  isRedemption?: boolean;
  /** A balance / instalment top-up leg of a partner order: the fee rode the first payment (Amendment A §3). */
  isPartnerTopUpLeg?: boolean;
  /** A request the school confirms before anything is charged. */
  isRequestToBook?: boolean;
};

const nonNegative = (n: number | undefined): number =>
  typeof n === "number" && Number.isFinite(n) && n > 0 ? n : 0;

/**
 * The booking fee the server will quote for this basket, estimated from the
 * disclosed rate so the Total is fee-inclusive from first paint (DMCC Act
 * 2025: a mandatory fee belongs in the headline price). Every guard mirrors a
 * "never charged" case in CreateBasketCheckoutSession.controller.ts, the base
 * mirrors `orderContractSubtotal`, and the rounding is the shared
 * `computeBookingFee`. Display only: the charged amount is always the
 * session's `bookingFeePence` — see `resolveDisplayedBookingFee`.
 */
export function estimateBookingFeePence(ctx: BookingFeeEstimateContext): number {
  if (
    !ctx.disclosure?.applies ||
    !(ctx.payNowMinor > 0) ||
    !ctx.hasBookingItem ||
    ctx.isRedemption ||
    ctx.isPartnerTopUpLeg ||
    ctx.isRequestToBook
  ) {
    return 0;
  }
  const base =
    ctx.payNowMinor + nonNegative(ctx.giftCardAppliedMinor) + nonNegative(ctx.laterBalanceMinor);
  return computeBookingFee(base, {
    percentBps: Number(ctx.disclosure.percentBps) || 0,
    fixedMinor: Number(ctx.disclosure.fixedMinor) || 0,
  });
}

/**
 * What the fee row and the Total show: the session's quote once there is one
 * (a quote of 0 is an answer — "no fee on this order" — not an absence),
 * else the first-paint estimate.
 */
export function resolveDisplayedBookingFee(
  quotedBookingFeePence: number | null | undefined,
  estimatedBookingFeePence: number,
): number {
  return quotedBookingFeePence ?? estimatedBookingFeePence;
}

/** Reads `bookingFeeDisclosure` off a checkout-config answer, defensively. */
export function readBookingFeeDisclosure(config: unknown): BookingFeeDisclosure | null {
  const d = (config as { bookingFeeDisclosure?: unknown } | null | undefined)?.bookingFeeDisclosure as
    | Partial<BookingFeeDisclosure>
    | null
    | undefined;
  if (!d || typeof d !== "object" || d.applies !== true) return null;
  return {
    applies: true,
    percentBps: Number(d.percentBps) || 0,
    fixedMinor: Number(d.fixedMinor) || 0,
    refundOnCustomerCancel: d.refundOnCustomerCancel === true,
  };
}

/**
 * The name for the fees folded into a total: the booking fee (the school's)
 * and the service fee (Baluu's) are different fees, so a badge summing them
 * names the one it holds, or says "fees" when it holds both.
 */
export function includedFeesLabel(bookingFeePence: number, serviceFeePence: number): string {
  const booking = bookingFeePence > 0;
  const service = serviceFeePence > 0;
  if (booking && service) return "fees";
  if (service) return "service fee";
  return "booking fee";
}
